import {
  addAllLabels,
  removeAllLabels,
  setSelectedLabels,
  updateSelectedLabels,
} from "redux/Modals/actions";
import { useDispatch } from "react-redux";
import { Fragment, useEffect, useState } from "react";
import TooltipWrapper from "views/uikit/TooltipWrapper";
import { IoMdInformationCircleOutline } from "react-icons/io";

import styles from "./style.module.scss";
import classNames from "classnames";
import OpenEyeIcon from "assets/icons/OpenEyeIcon";
import ClosedEyeIcon from "assets/icons/ClosedEyeIcon";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";

const SingleRow = ({
  singleLabel,
  selectedLabels,
  uniquenessScores,
  globalAction,
}: any) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const dispatch = useDispatch();

  const selectionCount = selectedLabels.filter((selectedLabel: any) =>
    singleLabel.labels.some(
      (label: any) => selectedLabel.id === label.id && selectedLabel.selected,
    ),
  ).length;

  const handleTriggerClick = () => {
    if (isExpanded) {
      dispatch(removeAllLabels(singleLabel.labels) as any);
    } else {
      dispatch(addAllLabels(singleLabel.labels) as any);
    }
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    if (globalAction.type === "HIDE_ALL") {
      setIsExpanded(false);
    } else if (globalAction.type === "SHOW_ALL") {
      setIsExpanded(true);
    }
  }, [globalAction]);

  const handleLabelSelection = (
    labelID: string,
    boundingBox: number[],
    color: string,
  ) => {
    dispatch(
      updateSelectedLabels(
        labelID,
        boundingBox,
        color,
        singleLabel.categoryName,
      ) as any,
    );
  };

  const renderSubLabelInfo = (subLabel: any, index: number) => {
    const uniquenessScore: number | null =
      uniquenessScores.find((item: any) => item.objectId === subLabel.id)
        ?.uniquenessScore || null;

    if (!subLabel.id) {
      return <noscript />;
    }

    let objectSizeLine = <noscript />;
    let uniquenessScoreLine = <noscript />;

    if (subLabel.bounding_box) {
      const [, , width, height] = subLabel.bounding_box;
      const objectSizeText = `${height}x${width}`;

      objectSizeLine = <span>{`object_size: ${objectSizeText}`}</span>;
    }

    if (typeof uniquenessScore === "number") {
      uniquenessScoreLine = (
        <span>{`uniqueness_score: ${uniquenessScore}`}</span>
      );
    }

    const content = (
      <div className={styles.objectToolTipTextContainer}>
        <span>{`object_id: ${subLabel.id}`}</span>
        {objectSizeLine}
        {uniquenessScoreLine}
      </div>
    );

    return (
      <div
        className={styles.infoIcon}
        onClick={(e) => e.stopPropagation()}
        data-tooltip-id={"image-details-object-info-tooltip" + index}
      >
        <IoMdInformationCircleOutline size="16px" />
        <TooltipWrapper
          maxWidth="max-content"
          id={"image-details-object-info-tooltip" + index}
          content={content}
        />
      </div>
    );
  };

  return (
    <Fragment>
      <div className={styles.singleLableRow}>
        <span
          className={styles.objectLabel}
          style={{ color: singleLabel.color }}
          onClick={handleTriggerClick}
        >
          {singleLabel?.categoryName}{" "}
          {`(${selectionCount}/${singleLabel?.labels?.length})`}
        </span>
        <div className={styles.actionButtons}>
          <div className={styles.triggerIcon} onClick={handleTriggerClick}>
            {isExpanded ? <OpenEyeIcon /> : <ClosedEyeIcon />}
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className={styles.subRows}>
          {singleLabel?.labels?.map((subLabel: any, index: number) => {
            const subLabelID = subLabel.id;
            const subLabelDisplayNumber = index + 1;
            const isSubIDSelected = selectedLabels?.some(
              (label: any) => label.id === subLabelID && label.selected,
            );

            const subLabelClassNames = classNames(styles.subLabel, {
              [styles.greyColorText]: !isSubIDSelected,
            });

            return (
              <div
                className={styles.singleLableRow}
                key={index}
                onClick={() =>
                  handleLabelSelection(
                    subLabel.id,
                    subLabel.bounding_box,
                    singleLabel.color,
                  )
                }
              >
                <span className={subLabelClassNames}>
                  {subLabel?.category_display_name} {subLabelDisplayNumber}
                  {renderSubLabelInfo(subLabel, index)}
                </span>

                <div className={styles.viewLabelButton}>
                  {isSubIDSelected ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Fragment>
  );
};

const ObjectsBox = ({ selectedLabels, labels, uniquenessScores }: any) => {
  const [globalAction, setGlobalAction] = useState({
    type: "SHOW_ALL",
    timestamp: 0,
  });

  const updateLabelsSelectedValue = (selected: boolean) =>
    dispatch(
      setSelectedLabels(selectedLabels.map((l: any) => ({ ...l, selected }))),
    );

  const dispatch = useDispatch();
  const selectAllLabels = () => {
    setGlobalAction({ type: "SHOW_ALL", timestamp: Date.now() });
    updateLabelsSelectedValue(true);
  };

  const hideAllLabels = () => {
    setGlobalAction({ type: "HIDE_ALL", timestamp: Date.now() });
    updateLabelsSelectedValue(false);
  };
  return (
    <div className={styles.objectsContainer}>
      <div className={styles.row}>
        <div className={styles.title}>Objects</div>
        <div className={styles.buttons}>
          <span className={styles.link} onClick={selectAllLabels}>
            Show All
          </span>
          <span className={styles.link} onClick={hideAllLabels}>
            Hide All
          </span>
        </div>
      </div>
      {labels.map((singleLabel: any, index: number) => (
        <SingleRow
          key={index}
          globalAction={globalAction}
          singleLabel={singleLabel}
          selectedLabels={selectedLabels}
          uniquenessScores={uniquenessScores}
        />
      ))}
    </div>
  );
};

export default ObjectsBox;
